import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = {
  unit_name: "Walk-in 1",
  temperature: "",
  status: "pass",
  notes: "",
};

// Note: rendered as "Temperature logs" in the sidebar — the backend route
// (/api/fridge-logs) and data-testids are kept stable for backward compat.

export default function FridgeLogs() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => setItems((await api.get("/fridge-logs")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      unit_name: r.unit_name,
      temperature: String(r.temperature),
      status: r.status,
      notes: r.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, temperature: Number(form.temperature) };
      if (editingId) {
        await api.put(`/fridge-logs/${editingId}`, body);
        toast.success("Log updated");
      } else {
        await api.post("/fridge-logs", body);
        toast.success("Temperature logged");
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/fridge-logs/${id}`); toast.success("Log removed"); load(); };

  const fails = items.filter((i) => i.status === "fail").length;
  const passRate = items.length ? Math.round(((items.length - fails) / items.length) * 100) : 100;

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-[1400px]" data-testid="fridge-page">
      <PageHeader
        eyebrow="Cold-chain & ambient compliance"
        title="Temperature logs"
        description="Track fridges, freezers, hot-holds and room temperatures across every site. Catch failures before inspectors do."
        actions={
          <Button data-testid="fridge-add-button" onClick={openCreate} className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] h-10 px-5">
            <Plus className="w-4 h-4 mr-2" /> Log temperature
          </Button>
        }
        testid="fridge-header"
      />

      <SummaryBar items={[
        { label: "Checks logged", value: items.length, testid: "stat-total" },
        { label: "Pass rate", value: `${passRate}%`, testid: "stat-passrate" },
        { label: "Failures", value: fails, tone: fails ? "alert" : "success", testid: "stat-fails" },
      ]} />

      <DataTable
        empty="No temperature logs yet. Hit “Log temperature” to add the first reading."
        head={["When", "Location", "Temp", "Recorded by", "Status", "Notes", ""]}
        rows={items}
        testid="fridge-table"
        render={(i, idx) => (
          <tr key={i.id} data-testid={`fridge-row-${idx}`} className="border-t border-[#e5e2d9] hover:bg-[#efece6]/40">
            <td className="px-4 py-3 text-sm whitespace-nowrap">{new Date(i.recorded_at).toLocaleString()}</td>
            <td className="px-4 py-3 text-sm">{i.unit_name}</td>
            <td className="px-4 py-3 text-sm font-mono">{i.temperature}°C</td>
            <td className="px-4 py-3 text-sm text-[#57534e]">{i.recorded_by}</td>
            <td className="px-4 py-3">
              <StatusPill tone={i.status === "pass" ? "success" : "alert"}>{i.status}</StatusPill>
            </td>
            <td className="px-4 py-3 text-sm text-[#57534e] max-w-[260px] truncate">{i.notes || "—"}</td>
            <td className="px-4 py-3 text-right">
              <div className="inline-flex items-center gap-3">
                <button data-testid={`fridge-edit-${idx}`} onClick={() => openEdit(i)} className="text-[#8a8476] hover:text-[#1b3b36]" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`fridge-delete-${idx}`} onClick={() => del(i.id)} className="text-[#8a8476] hover:text-[#d94f3d]" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </td>
          </tr>
        )}
      />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(empty); } }}>
        <DialogContent className="bg-white border-[#e5e2d9] rounded-[4px]">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Edit temperature log" : "New temperature log"}
            </DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submit} data-testid="fridge-form">
            <Field label="Location / unit">
              <Input data-testid="fridge-unit-input" value={form.unit_name} required onChange={(e) => setForm((f) => ({ ...f, unit_name: e.target.value }))} className="bg-white rounded-[4px]" placeholder="Walk-in 1, Bakery freezer, Shop floor…" />
            </Field>
            <Field label="Temperature (°C)">
              <Input type="number" step="0.1" data-testid="fridge-temp-input" value={form.temperature} required onChange={(e) => setForm((f) => ({ ...f, temperature: e.target.value }))} className="bg-white rounded-[4px]" />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="fridge-status-select" className="bg-white rounded-[4px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">Pass</SelectItem>
                  <SelectItem value="fail">Fail</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes">
              <Textarea data-testid="fridge-notes-input" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="bg-white rounded-[4px]" />
            </Field>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="fridge-submit-button" className="bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px]">
                {busy ? "Saving…" : editingId ? "Save changes" : "Save log"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function SummaryBar({ items }) {
  return (
    <div className="grid grid-cols-3 border border-[#e5e2d9] bg-white mb-6">
      {items.map((s, i) => (
        <div key={s.label} data-testid={s.testid} className={`p-5 ${i < items.length - 1 ? "border-r border-[#e5e2d9]" : ""}`}>
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#8a8476]">{s.label}</div>
          <div className={`font-display text-3xl mt-2 ${s.tone === "alert" ? "text-[#d94f3d]" : "text-[#1c1917]"}`}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

export function DataTable({ head, rows, render, empty, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#e5e2d9] overflow-x-auto">
      {rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-[#8a8476]">{empty}</div>
      ) : (
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="bg-[#efece6]">
              {head.map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[10px] uppercase tracking-[0.22em] text-[#8a8476] font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{rows.map(render)}</tbody>
        </table>
      )}
    </div>
  );
}
